import type { Practitioner, Reference } from '@medplum/fhirtypes'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { cn } from '@seen/ui'
import { cva, type VariantProps } from 'class-variance-authority'
import type { JSX } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { IconFax } from '#src/components/icons/IconFax.tsx'
import { useRead } from '#src/hooks/useRead.ts'
import { useReference } from '#src/hooks/useReference.ts'

interface PractitionerNameplateProps extends VariantProps<typeof variants> {
  practitioner: Reference<Practitioner> | string
  className?: string
}

export function PractitionerNameplate({
  practitioner,
  size,
  className,
  faxNumber,
}: PractitionerNameplateProps & { faxNumber?: string }): JSX.Element {
  const practitionerReference = useReference(practitioner)
  const { data: instance } = useRead(
    ExtendedPractitioner,
    practitionerReference
  )
  return (
    <div className={cn(variants({ size, className }))}>
      <Avatar
        profile={practitionerReference}
        size={!size || size === 5 ? 'icon' : size}
      />
      <div className="flex flex-col">
        <p>
          {instance?.name}
          {!!instance?.roles?.length && (
            <span className="ml-1 text-foreground-secondary">
              {instance.roles.join(', ')}
            </span>
          )}
        </p>
        {faxNumber && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <IconFax className="size-3 shrink-0" /> {faxNumber}
          </span>
        )}
      </div>
    </div>
  )
}

const variants = cva('flex items-center', {
  variants: {
    size: {
      5: 'gap-2',
      3.5: 'gap-1',
      3: 'gap-1',
    },
  },
  defaultVariants: {
    size: 5,
  },
})
